import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Plus, 
  Bank, 
  Calendar,
  CurrencyCircleDollar,
  Clock,
  Check,
  Warning,
  Pencil,
  CaretDown,
  CaretUp
} from '@phosphor-icons/react';
import { Trash2, CreditCard, Wallet } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

export default function LoanManagement() {
  const { user } = useAuth();
  const [loans, setLoans] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [editingLoan, setEditingLoan] = useState(null);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [expandedLoan, setExpandedLoan] = useState(null);
  const [loanPayments, setLoanPayments] = useState({});
  const [exchangeRates, setExchangeRates] = useState(null);
  
  const [formData, setFormData] = useState({
    loan_name: '',
    bank_name: '',
    loan_number: '',
    currency: 'EUR',
    total_amount: '',
    total_installments: '',
    monthly_payment: '',
    interest_rate: '',
    start_date: new Date().toISOString().split('T')[0],
    purpose: '',
    notes: '',
  });

  const [paymentData, setPaymentData] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    amount: '',
    principal: '',
    interest: '',
    payment_method: 'bank_transfer',
    notes: '',
  });

  const CURRENCY_OPTIONS = [
    { value: 'TRY', label: '₺ TRY', symbol: '₺' },
    { value: 'EUR', label: '€ EUR', symbol: '€' },
    { value: 'USD', label: '$ USD', symbol: '$' },
  ];

  const PAYMENT_METHODS = [
    { value: 'bank_transfer', label: 'Havale/EFT' },
    { value: 'cash', label: 'Nakit' },
    { value: 'check', label: 'Çek' },
  ];

  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'admin' || user?.role === 'manager';

  useEffect(() => {
    fetchData();
    fetchExchangeRates();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [loansRes, summaryRes] = await Promise.all([
        api.get('/loans'),
        api.get('/loans/summary')
      ]);
      setLoans(loansRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const fetchExchangeRates = async () => {
    try {
      const res = await api.get('/exchange-rates');
      setExchangeRates(res.data.rates);
    } catch (error) {
      console.error('Döviz kurları yüklenemedi:', error);
    }
  };

  const convertToTRY = (amount, currency) => {
    if (!exchangeRates || currency === 'TRY') return null;
    const rate = exchangeRates[currency]?.sell || exchangeRates[currency]?.mid;
    if (!rate) return null;
    return amount * rate;
  };

  const fetchLoanPayments = async (loanId) => {
    try {
      const res = await api.get(`/loans/${loanId}/payments`);
      setLoanPayments(prev => ({ ...prev, [loanId]: res.data }));
    } catch (error) {
      toast.error('Ödemeler yüklenemedi');
    }
  };

  const toggleExpand = async (loanId) => {
    if (expandedLoan === loanId) {
      setExpandedLoan(null);
    } else {
      setExpandedLoan(loanId);
      if (!loanPayments[loanId]) {
        await fetchLoanPayments(loanId);
      }
    }
  };

  const resetForm = () => {
    setEditingLoan(null);
    setFormData({
      loan_name: '',
      bank_name: '',
      loan_number: '',
      currency: 'EUR',
      total_amount: '',
      total_installments: '',
      monthly_payment: '',
      interest_rate: '',
      start_date: new Date().toISOString().split('T')[0],
      purpose: '',
      notes: '',
    });
  };

  const resetPaymentForm = () => {
    setPaymentData({
      payment_date: new Date().toISOString().split('T')[0],
      amount: '',
      principal: '',
      interest: '',
      payment_method: 'bank_transfer',
      notes: '',
    });
  };

  const handleSubmit = async () => {
    if (!formData.loan_name || !formData.bank_name || !formData.total_amount || !formData.total_installments || !formData.monthly_payment) {
      toast.error('Zorunlu alanları doldurun');
      return;
    }

    try {
      const payload = {
        ...formData,
        total_amount: parseFloat(formData.total_amount),
        total_installments: parseInt(formData.total_installments),
        monthly_payment: parseFloat(formData.monthly_payment),
        interest_rate: formData.interest_rate ? parseFloat(formData.interest_rate) : null,
      };

      if (editingLoan) {
        await api.put(`/loans/${editingLoan.id}`, payload);
        toast.success('Kredi güncellendi');
      } else {
        await api.post('/loans', payload);
        toast.success('Kredi eklendi');
      }

      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const handlePaymentSubmit = async () => {
    if (!paymentData.amount || !paymentData.payment_date) {
      toast.error('Tutar ve tarih zorunludur');
      return;
    }

    try {
      const payload = {
        ...paymentData,
        amount: parseFloat(paymentData.amount),
        principal: paymentData.principal ? parseFloat(paymentData.principal) : null,
        interest: paymentData.interest ? parseFloat(paymentData.interest) : null,
      };

      await api.post(`/loans/${selectedLoan.id}/payments`, payload);
      toast.success('Ödeme kaydedildi');

      setIsPaymentDialogOpen(false);
      resetPaymentForm();
      fetchData();
      fetchLoanPayments(selectedLoan.id);
    } catch (error) {
      toast.error('Ödeme kaydedilemedi');
    }
  };

  const handleEdit = (loan) => {
    setEditingLoan(loan);
    setFormData({
      loan_name: loan.loan_name,
      bank_name: loan.bank_name,
      loan_number: loan.loan_number || '',
      currency: loan.currency,
      total_amount: loan.total_amount.toString(),
      total_installments: loan.total_installments.toString(),
      monthly_payment: loan.monthly_payment.toString(),
      interest_rate: loan.interest_rate?.toString() || '',
      start_date: loan.start_date,
      purpose: loan.purpose || '',
      notes: loan.notes || '',
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (loanId) => {
    if (!confirm('Bu krediyi ve tüm ödemelerini silmek istediğinize emin misiniz?')) return;
    
    try {
      await api.delete(`/loans/${loanId}`);
      toast.success('Kredi silindi');
      fetchData();
    } catch (error) {
      toast.error('Silme başarısız');
    }
  };

  const handleDeletePayment = async (loanId, paymentId) => {
    if (!confirm('Bu ödemeyi silmek istediğinize emin misiniz?')) return;
    
    try {
      await api.delete(`/loans/${loanId}/payments/${paymentId}`);
      toast.success('Ödeme silindi');
      fetchData();
      fetchLoanPayments(loanId);
    } catch (error) {
      toast.error('Silme başarısız');
    }
  };

  const openPaymentDialog = (loan) => {
    setSelectedLoan(loan);
    setPaymentData({
      ...paymentData,
      amount: loan.monthly_payment.toString(),
    });
    setIsPaymentDialogOpen(true);
  };

  const formatCurrency = (value, currency = 'TRY') => {
    const symbols = { TRY: '₺', EUR: '€', USD: '$' };
    const symbol = symbols[currency] || currency;
    return `${symbol}${new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2 }).format(value || 0)}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('tr-TR');
  };

  const getProgressColor = (paid, total) => {
    const percent = (paid / total) * 100;
    if (percent >= 75) return 'bg-green-500';
    if (percent >= 50) return 'bg-blue-500';
    if (percent >= 25) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="loan-management">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold">Kredi Takibi</h1>
            <p className="text-muted-foreground">Yatırım kredileri ve taksit ödemeleri (Giderlere dahil edilmez)</p>
          </div>
          {isManager && (
            <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button data-testid="add-loan-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Kredi Ekle
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>{editingLoan ? 'Kredi Düzenle' : 'Yeni Kredi Ekle'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4 max-h-[70vh] overflow-y-auto pr-2">
                  <div>
                    <Label>Kredi Adı *</Label>
                    <Input
                      value={formData.loan_name}
                      onChange={(e) => setFormData({ ...formData, loan_name: e.target.value })}
                      placeholder="İş Bankası Euro Kredisi"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Banka *</Label>
                      <Input
                        value={formData.bank_name}
                        onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                        placeholder="İş Bankası"
                      />
                    </div>
                    <div>
                      <Label>Kredi Hesap No</Label>
                      <Input
                        value={formData.loan_number}
                        onChange={(e) => setFormData({ ...formData, loan_number: e.target.value })}
                        placeholder="6807-0159307"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Para Birimi *</Label>
                      <Select value={formData.currency} onValueChange={(v) => setFormData({ ...formData, currency: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CURRENCY_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Toplam Kredi Tutarı *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.total_amount}
                        onChange={(e) => setFormData({ ...formData, total_amount: e.target.value })}
                        placeholder="300000"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Toplam Taksit Sayısı *</Label>
                      <Input
                        type="number"
                        value={formData.total_installments}
                        onChange={(e) => setFormData({ ...formData, total_installments: e.target.value })}
                        placeholder="60"
                      />
                    </div>
                    <div>
                      <Label>Aylık Taksit Tutarı *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.monthly_payment}
                        onChange={(e) => setFormData({ ...formData, monthly_payment: e.target.value })}
                        placeholder="5972.77"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Faiz Oranı (%)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.interest_rate}
                        onChange={(e) => setFormData({ ...formData, interest_rate: e.target.value })}
                        placeholder="1.5"
                      />
                    </div>
                    <div>
                      <Label>Başlangıç Tarihi *</Label>
                      <Input
                        type="date"
                        value={formData.start_date}
                        onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label>Kredi Amacı</Label>
                    <Input
                      value={formData.purpose}
                      onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                      placeholder="Bina Yatırımı, Araç, Makine vs."
                    />
                  </div>
                  
                  <div>
                    <Label>Notlar</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Ek notlar..."
                      rows={2}
                    />
                  </div>
                  
                  <Button onClick={handleSubmit} className="w-full">
                    {editingLoan ? 'Güncelle' : 'Kaydet'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Özet Kartları */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(summary.by_currency || {}).map(([currency, data]) => {
              const tryAmount = convertToTRY(data.total_debt, currency);
              const tryMonthly = convertToTRY(data.monthly_payment, currency);
              return (
                <Card key={currency} className="border-2 border-blue-200 bg-blue-50">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-blue-700 font-medium uppercase">{currency} Kredileri</p>
                        <p className="text-2xl font-bold text-blue-700">
                          {formatCurrency(data.total_debt, currency)}
                        </p>
                        {tryAmount && (
                          <p className="text-lg font-semibold text-green-700">
                            ≈ {formatCurrency(tryAmount, 'TRY')}
                          </p>
                        )}
                        <p className="text-xs text-blue-600 mt-1">
                          {data.loan_count} kredi • Aylık: {formatCurrency(data.monthly_payment, currency)}
                          {tryMonthly && <span className="text-green-600"> (≈{formatCurrency(tryMonthly, 'TRY')})</span>}
                        </p>
                      </div>
                      <div className="p-3 bg-blue-200 rounded-full">
                        <Wallet className="w-6 h-6 text-blue-700" />
                      </div>
                    </div>
                    {exchangeRates && currency !== 'TRY' && exchangeRates[currency] && (
                      <div className="mt-2 pt-2 border-t border-blue-200">
                        <p className="text-xs text-blue-500">
                          Kur: 1 {currency} = ₺{exchangeRates[currency].sell?.toFixed(2)} (expressdoviz.com)
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
            
            <Card className="border-2 border-orange-200 bg-orange-50">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-orange-700 font-medium uppercase">Yaklaşan Ödemeler</p>
                    <p className="text-2xl font-bold text-orange-700">
                      {summary.upcoming_payments?.length || 0}
                    </p>
                    <p className="text-xs text-orange-600">Önümüzdeki 30 gün içinde</p>
                  </div>
                  <div className="p-3 bg-orange-200 rounded-full">
                    <Clock className="w-6 h-6 text-orange-700" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Yaklaşan Ödemeler */}
        {summary?.upcoming_payments?.length > 0 && (
          <Card className="border-orange-300">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2 text-orange-700">
                <Warning className="w-5 h-5" />
                Yaklaşan Ödemeler
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {summary.upcoming_payments.map((payment, idx) => {
                  const tryAmount = convertToTRY(payment.amount, payment.currency);
                  return (
                    <div key={idx} className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">{payment.loan_name}</p>
                          <p className="text-sm text-muted-foreground">{payment.bank_name}</p>
                        </div>
                        <Badge variant="outline">{payment.installment}</Badge>
                      </div>
                      <div className="mt-2 flex justify-between items-center">
                        <div>
                          <span className="text-lg font-bold">{formatCurrency(payment.amount, payment.currency)}</span>
                          {tryAmount && (
                            <span className="text-sm font-semibold text-green-700 ml-2">
                              ≈ {formatCurrency(tryAmount, 'TRY')}
                            </span>
                          )}
                        </div>
                        <span className="text-sm text-orange-600">{formatDate(payment.due_date)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Kredi Listesi */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Krediler ({loans.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : loans.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <CreditCard size={48} className="mx-auto mb-3 opacity-50" />
                <p>Henüz kredi kaydı yok</p>
              </div>
            ) : (
              <div className="space-y-4">
                {loans.map((loan) => (
                  <div key={loan.id} className="border rounded-lg overflow-hidden">
                    {/* Kredi Başlık */}
                    <div 
                      className={`p-4 cursor-pointer hover:bg-slate-50 ${loan.status === 'completed' ? 'bg-green-50' : ''}`}
                      onClick={() => toggleExpand(loan.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <Bank className="w-5 h-5 text-blue-700" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold">{loan.loan_name}</h3>
                              {loan.status === 'completed' && (
                                <Badge className="bg-green-100 text-green-700">Tamamlandı</Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {loan.bank_name} {loan.loan_number && `• ${loan.loan_number}`}
                              {loan.purpose && ` • ${loan.purpose}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-right">
                            <p className="text-sm text-muted-foreground">Kalan Borç</p>
                            <p className="text-xl font-bold">{formatCurrency(loan.remaining_amount, loan.currency)}</p>
                            {convertToTRY(loan.remaining_amount, loan.currency) && (
                              <p className="text-sm font-semibold text-green-700">
                                ≈ {formatCurrency(convertToTRY(loan.remaining_amount, loan.currency), 'TRY')}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-muted-foreground">Aylık Taksit</p>
                            <p className="font-medium">{formatCurrency(loan.monthly_payment, loan.currency)}</p>
                            {convertToTRY(loan.monthly_payment, loan.currency) && (
                              <p className="text-xs text-green-600">
                                ≈ {formatCurrency(convertToTRY(loan.monthly_payment, loan.currency), 'TRY')}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-muted-foreground">Taksit</p>
                            <p className="font-medium">{loan.paid_installments}/{loan.total_installments}</p>
                          </div>
                          {expandedLoan === loan.id ? (
                            <CaretUp className="w-5 h-5 text-muted-foreground" />
                          ) : (
                            <CaretDown className="w-5 h-5 text-muted-foreground" />
                          )}
                        </div>
                      </div>
                      
                      {/* Progress Bar */}
                      <div className="mt-3 h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${getProgressColor(loan.paid_installments, loan.total_installments)} transition-all`}
                          style={{ width: `${(loan.paid_installments / loan.total_installments) * 100}%` }}
                        />
                      </div>
                    </div>
                    
                    {/* Detay Bölümü */}
                    {expandedLoan === loan.id && (
                      <div className="border-t bg-slate-50 p-4">
                        <div className="flex justify-between items-center mb-4">
                          <h4 className="font-medium">Ödeme Geçmişi</h4>
                          <div className="flex gap-2">
                            {isManager && loan.status !== 'completed' && (
                              <Button size="sm" onClick={(e) => { e.stopPropagation(); openPaymentDialog(loan); }}>
                                <Plus className="w-4 h-4 mr-1" />
                                Ödeme Ekle
                              </Button>
                            )}
                            {isManager && (
                              <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleEdit(loan); }}>
                                <Pencil className="w-4 h-4 mr-1" />
                                Düzenle
                              </Button>
                            )}
                            {isAdmin && (
                              <Button variant="destructive" size="sm" onClick={(e) => { e.stopPropagation(); handleDelete(loan.id); }}>
                                <Trash2 className="w-4 h-4 mr-1" />
                                Sil
                              </Button>
                            )}
                          </div>
                        </div>
                        
                        {loanPayments[loan.id]?.length > 0 ? (
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Taksit</TableHead>
                                <TableHead>Tarih</TableHead>
                                <TableHead>Ödeme Şekli</TableHead>
                                <TableHead className="text-right">Anapara</TableHead>
                                <TableHead className="text-right">Faiz</TableHead>
                                <TableHead className="text-right">Toplam</TableHead>
                                <TableHead></TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {loanPayments[loan.id].map((payment) => (
                                <TableRow key={payment.id}>
                                  <TableCell>
                                    <Badge variant="outline">{payment.installment_number}</Badge>
                                  </TableCell>
                                  <TableCell>{formatDate(payment.payment_date)}</TableCell>
                                  <TableCell>
                                    {PAYMENT_METHODS.find(m => m.value === payment.payment_method)?.label || payment.payment_method}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {payment.principal ? formatCurrency(payment.principal, loan.currency) : '-'}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {payment.interest ? formatCurrency(payment.interest, loan.currency) : '-'}
                                  </TableCell>
                                  <TableCell className="text-right font-semibold">
                                    {formatCurrency(payment.amount, loan.currency)}
                                  </TableCell>
                                  <TableCell>
                                    {isAdmin && (
                                      <Button 
                                        variant="ghost" 
                                        size="sm"
                                        onClick={(e) => { e.stopPropagation(); handleDeletePayment(loan.id, payment.id); }}
                                      >
                                        <Trash2 className="w-4 h-4 text-destructive" />
                                      </Button>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        ) : (
                          <p className="text-center text-muted-foreground py-4">Henüz ödeme yapılmamış</p>
                        )}
                        
                        {loan.next_payment_date && loan.status !== 'completed' && (
                          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-yellow-700" />
                              <span className="text-sm text-yellow-700">
                                Sonraki Ödeme: <strong>{formatDate(loan.next_payment_date)}</strong> - 
                                {formatCurrency(loan.monthly_payment, loan.currency)}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Ödeme Ekleme Dialog */}
        <Dialog open={isPaymentDialogOpen} onOpenChange={(open) => { setIsPaymentDialogOpen(open); if (!open) resetPaymentForm(); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Taksit Ödemesi Ekle</DialogTitle>
            </DialogHeader>
            {selectedLoan && (
              <div className="space-y-4 mt-4">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="font-medium">{selectedLoan.loan_name}</p>
                  <p className="text-sm text-muted-foreground">
                    Taksit {selectedLoan.paid_installments + 1}/{selectedLoan.total_installments}
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Ödeme Tarihi *</Label>
                    <Input
                      type="date"
                      value={paymentData.payment_date}
                      onChange={(e) => setPaymentData({ ...paymentData, payment_date: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Toplam Tutar *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={paymentData.amount}
                      onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Anapara</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={paymentData.principal}
                      onChange={(e) => setPaymentData({ ...paymentData, principal: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Faiz</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={paymentData.interest}
                      onChange={(e) => setPaymentData({ ...paymentData, interest: e.target.value })}
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Ödeme Şekli</Label>
                  <Select value={paymentData.payment_method} onValueChange={(v) => setPaymentData({ ...paymentData, payment_method: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_METHODS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Not</Label>
                  <Textarea
                    value={paymentData.notes}
                    onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                    placeholder="Ödeme notu..."
                    rows={2}
                  />
                </div>
                
                <Button onClick={handlePaymentSubmit} className="w-full">
                  Ödemeyi Kaydet
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
